import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnInit,
  OnChanges,
  SimpleChanges,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { finalize } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatTooltip, MatTooltipModule } from '@angular/material/tooltip';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { WhitelistedWallet, WhitelistedWalletSourceEnum } from '../merchant-whitelisted-wallets.models';
import { WalletService } from '@olv/ui-shared/services/wallet.service';
import { HasPermissionDirective } from '@olv/ui-shared/directives/has-permission.directive';
import { TaskResolutionEnum } from '@olv/ui-shared/enums/task.enums';
import { Resources, Scopes } from '@olv/ui-shared/models/settlement-permissions.model';
import { WalletOwnerTypeEnum } from '@olv/ui-shared/models/wallet.model';
import { AuthService } from '@olv/ui-shared/services/auth.service';
import { MerchantService } from '@olv/ui-shared/services/merchant.service';
import { formatDateStandard } from '@olv/ui-shared/utils/dateFormat.utils';
import { FormErrorService } from '@olv/ui-shared/services/form-error.service';
import { ValidationService } from '@olv/ui-shared/services/validation.service';
import { shortenAddress } from '@olv/ui-shared/utils/shortenAddress.utils';
import { LoadingSpinnerComponent } from '@olv/ui-shared/ui-shared/loading-spinner/loading-spinner.component';
import { AppContextService } from '@olv/ui-shared/services/app-context.service';
import { StateBadgeComponent } from '@olv/ui-shared/ui-shared/state-badge/state-badge.component';
import { MatDividerModule } from '@angular/material/divider';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { OtpVerificationDialogComponent } from '@olv/ui-shared/ui-shared/otp-verification-dialog/otp-verification-dialog.component';
import { AttachmentViewDialogComponent } from '@olv/ui-shared/ui-shared/attachment-view-dialog/attachment-view-dialog.component';
import { ToastService } from '@olv/ui-shared/ui-shared/toast/toast.service';
import { ButtonComponent, ButtonState, toButtonState } from '@olv/ui-shared/ui-shared/button/button.component';

@Component({
  selector: 'app-merchant-whitelisted-wallet-details',
  standalone: true,
  imports: [
    MatIconModule,
    CommonModule,
    FormsModule,
    MatTooltipModule,
    HasPermissionDirective,
    ReactiveFormsModule,
    LoadingSpinnerComponent,
    MatTooltipModule,
    StateBadgeComponent,
    MatDividerModule,
    ButtonComponent,
  ],
  templateUrl: './merchant-whitelisted-wallet-details.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './merchant-whitelisted-wallet-details.component.scss',
})
export class MerchantWhitelistedWalletDetailsComponent implements OnInit, OnChanges {
  protected readonly Resources = Resources;
  protected readonly Scopes = Scopes;
  protected readonly formatDateStandard = formatDateStandard;
  protected readonly shortenAddress = shortenAddress;

  @Output() closed = new EventEmitter<void>();
  @Output() refresh = new EventEmitter<void>();
  @Input() wallet!: WhitelistedWallet;
  @Input() isTaskContext = false;
  @Input() showActions = true;

  attachment = '';
  copyText = 'Copy';
  isloading = false;
  whitelistedError = '';
  isProcessing = false;
  attachmentType: string | null = null;
  imageDataUrl: string | null = null;
  blobObjectUrl: string | null = null;
  safeUrl: SafeResourceUrl | null = null;
  isSettlementApp = false;

  private readonly appCtx = inject(AppContextService);
  private fb = inject(FormBuilder);
  private formErrorService = inject(FormErrorService);
  private merchantService = inject(MerchantService);
  private walletService = inject(WalletService);
  private sanitizer = inject(DomSanitizer);
  private auth = inject(AuthService);
  private dialog = inject(MatDialog);
  private readonly toastr = inject(ToastService);

  merchantForm!: FormGroup;

  get permissionResource() {
    if (this.wallet?.ownerType === WalletOwnerTypeEnum.Merchant || !this.isSettlementApp) {
      return Resources.MerchantWhitelistedWallets;
    } else {
      return Resources.SystemWhitelistedWallets;
    }
  }

  get canApprove(): boolean {
    return this.auth.hasPermission(this.permissionResource, Scopes.Approve);
  }

  get canReject(): boolean {
    return this.auth.hasPermission(this.permissionResource, Scopes.Reject);
  }

  get canApproveOrReject(): boolean {
    return this.canApprove || this.canReject;
  }

  get approveButtonState(): ButtonState {
    return toButtonState(this.isProcessing, !this.wallet?.canApproveOrReject);
  }

  get processingButtonState(): ButtonState {
    return toButtonState(this.isProcessing, false);
  }

  ngOnInit(): void {
    const appName = this.appCtx.getAppName();
    this.isSettlementApp = appName === 'settlement';
    this.initForm();
  }

  private initForm(): void {
    const getInitialComment = (val?: string) => {
      if (val) return val;
      return this.wallet?.status !== 'Pending' ? 'No Comments' : '';
    };

    const initValue = getInitialComment(this.wallet?.comments);

    this.merchantForm = this.fb.group({
      commentText: [
        {
          value: initValue,
          disabled: this.wallet.status !== 'Pending' || !this.canApproveOrReject,
        },
        [ValidationService.textValidator(0, 500, '^[\\p{L}\\p{N} .,;:!?()\'"\\-_/\\\\%]*$')],
      ],
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    const walletChange = changes['wallet'];
    if (!this.merchantForm) this.initForm();
    if (!walletChange) return;

    const newWallet = walletChange.currentValue as WhitelistedWallet;
    const previousWallet = walletChange.previousValue as WhitelistedWallet;

    if (previousWallet && previousWallet.id !== newWallet.id) {
      this.merchantForm.reset();
      this.whitelistedError = '';
    }

    const commentValue = this.getCommentValue(
      newWallet.comments,
      newWallet.status,
      this.merchantForm.get('commentText')?.value,
    );

    this.merchantForm.patchValue(
      {
        commentText: commentValue,
      },
      { emitEvent: false },
    );

    this.updateForm(newWallet);
  }

  private updateForm(wallet: WhitelistedWallet): void {
    const isPending = wallet.status === 'Pending';

    const commentText = this.merchantForm.get('commentText');
    const shouldEnableComment = isPending && this.canApproveOrReject;

    if (shouldEnableComment) {
      commentText?.enable({ emitEvent: false });
    } else {
      commentText?.disable({ emitEvent: false });
    }
  }

  private getCommentValue(newVal: string | undefined, status: string, currentValue: string): string {
    if (newVal) return newVal;
    if (status !== 'Pending') return 'No Comments';
    return currentValue || '';
  }

  getFieldError(controlName: string, fieldName: string, customMessages?: { [key: string]: string }): string {
    return this.formErrorService.getErrorMessage(this.merchantForm.get(controlName), fieldName, customMessages);
  }

  onClose(): void {
    this.closed.emit();
  }

  getAttachment(mediaId: string): void {
    this.isloading = true;

    this.merchantService.getFileData(mediaId).subscribe({
      next: (res) => {
        const fileData = (res.fileData || '').replace(/\s/g, '');
        const contentType = res.contentType || 'application/octet-stream';

        let imageDataUrl: string | null = null;

        if (contentType.startsWith('image/')) {
          imageDataUrl = `data:${contentType};base64,${fileData}`;
        }

        const blob = this.base64ToBlob(fileData, contentType);
        const blobUrl = URL.createObjectURL(blob);
        const safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(blobUrl);

        this.isloading = false;

        this.dialog.open(AttachmentViewDialogComponent, {
          maxWidth: '95vw',
          maxHeight: '95vh',
          panelClass: 'custom-dialog-container',
          autoFocus: false,
          disableClose: false,
          data: {
            attachmentType: contentType,
            imageDataUrl,
            safeUrl,
          },
        });
      },
      error: () => {
        this.isloading = false;
      },
    });
  }

  base64ToBlob(b64: string, type: string): Blob {
    const byteStr = atob(b64);
    const len = byteStr.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) bytes[i] = byteStr.charCodeAt(i);
    return new Blob([bytes], { type });
  }

  handleWalletAction(
    action: 'approve' | 'reject' | 'revoke',
    otp?: string,
    dialogRef?: MatDialogRef<OtpVerificationDialogComponent>,
  ): void {
    if (!this.wallet?.id) return;
    if (action === 'approve' && !otp) return;
    if (action !== 'approve') {
      this.isloading = true;
    }

    this.isProcessing = true;
    this.whitelistedError = '';

    if (dialogRef) {
      dialogRef.componentInstance.setLoadingState(true);
      dialogRef.disableClose = true;
    }

    const comment = this.merchantForm.get('commentText')?.value?.trim() || '';

    let update$;

    if (this.wallet.ownerType === WalletOwnerTypeEnum.Merchant || !this.isSettlementApp) {
      update$ = this.merchantService.updateWhitelistedWalletStatus(this.wallet.id, action, comment, otp);
    } else if (this.wallet.ownerType === WalletOwnerTypeEnum.System) {
      update$ = this.walletService.updateSystemWhitelistedWalletStatus(this.wallet.id, action, comment, otp);
    } else {
      this.isProcessing = false;
      return;
    }

    update$
      .pipe(
        finalize(() => {
          this.isProcessing = false;
          this.isloading = false;
          if (dialogRef) {
            dialogRef.disableClose = false;
            dialogRef.componentInstance.setLoadingState(false);
          }
        }),
      )
      .subscribe({
        next: () => {
          this.refresh.emit();
          if (dialogRef) dialogRef.close();
          this.onClose();
          if (action !== 'approve') {
            const successMessage = action === 'reject' ? 'Wallet rejected successfully' : 'Wallet revoked successfully';

            this.toastr.success(successMessage);
          }
        },
        error: (err) => {
          if (err.isOtpError && dialogRef) {
            dialogRef.componentInstance.otpForm.reset();
            dialogRef.componentInstance.focusInput(0);
            dialogRef.componentInstance.setErrorState(err.error?.message);
            return;
          }
          this.isloading = false;

          if (dialogRef) dialogRef.close();

          this.whitelistedError = err.error?.message || 'Failed to process wallet. Please try again.';

          if (action !== 'approve') {
            const errorMessage = action === 'reject' ? 'Failed to reject wallet' : 'Failed to revoke wallet';

            this.toastr.error(errorMessage, this.whitelistedError);
          }
        },
      });
  }

  openOTP(): void {
    const dialogRef = this.dialog.open(OtpVerificationDialogComponent, {
      width: '600px',
      maxWidth: '95vw',
      maxHeight: '95vh',
      autoFocus: false,
      disableClose: false,
      panelClass: 'custom-dialog-container',
      data: {
        title: 'Verify Whitelisted Wallet',
      },
    });

    const sub = dialogRef.componentInstance.submitOtp.subscribe((otp) => {
      this.handleWalletAction('approve', otp, dialogRef);
    });

    dialogRef.afterClosed().subscribe(() => {
      sub.unsubscribe();
    });
  }

  resolutionToString(value: TaskResolutionEnum): string {
    return TaskResolutionEnum[value];
  }

  copyToClipboard(value: string, tooltip: MatTooltip) {
    navigator.clipboard.writeText(value).then(() => {
      this.copyText = 'Copied';
      tooltip.show();
      setTimeout(() => {
        this.copyText = 'Copy';
      }, 1500);
    });
  }

  protected readonly WhitelistedWalletSourceEnum = WhitelistedWalletSourceEnum;
}
